// src/sot/primitives/TopBarCenter.jsx
import React from "react";
import { X as XIcon, ChevronLeft } from "lucide-react";
import "@/styles/afrimed.css";
import "@/sot/afrimed-design-system.css";

/**
 * TopBarCenter — rails aligned, title truly centered, no borders
 * ✅ Fix: 3-col grid reserves space for left/right so long titles never overlap back button.
 */
export default function TopBarCenter({
  title = "",
  subtitle = "",
  onBack,
  right = null,
  trailing = null, // ✅ alias support (older callsites)
  className = "",
  margin = "mb-4 md:mb-5",
  close = false,
  titleClassName = "text-headline-small text-on-surface",
  subtitleClassName = "text-body-medium text-on-surface-var",
  nakedBack = false,
  backIconSrc = "",
  rowClassName = "",
  showBack = true,
  backLabel,
  wrapTitle = false,
}) {
  const leftSlotRef = React.useRef(null);
  const rightSlotRef = React.useRef(null);
  const [sideWidth, setSideWidth] = React.useState(44);

  const leftBtnClasses = nakedBack
    ? "inline-flex items-center justify-center h-10 w-10"
    : [
        "inline-flex items-center justify-center",
        "topbar-back",
        "h-10 w-10 rounded-[12px]",
        "text-on-surface",
      ].join(" ");
  
  const rightSlot = right ?? trailing ?? null;

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const measureSlotWidth = (node) => {
      if (!node) return 44;
      const child = node.firstElementChild;
      const nodeWidth = node.getBoundingClientRect?.().width || 44;
      const childWidth = child?.getBoundingClientRect?.().width || 0;
      const childScrollWidth = child?.scrollWidth || 0;
      return Math.ceil(Math.max(nodeWidth, childWidth, childScrollWidth, 44));
    };

    const computeSideWidth = () => {
      const leftWidth = measureSlotWidth(leftSlotRef.current);
      const rightWidth = measureSlotWidth(rightSlotRef.current);
      setSideWidth(Math.max(44, leftWidth, rightWidth));
    };

    computeSideWidth();

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(computeSideWidth);
      if (leftSlotRef.current) observer.observe(leftSlotRef.current);
      if (rightSlotRef.current) observer.observe(rightSlotRef.current);
    }

    window.addEventListener("resize", computeSideWidth);
    return () => {
      window.removeEventListener("resize", computeSideWidth);
      observer?.disconnect();
    };
  }, [rightSlot, close, nakedBack, backIconSrc, showBack]);

  return (
    <header className={`w-full ${className} ${margin}`}>
      <div style={{ height: "env(safe-area-inset-top)" }} />
      {/* ✅ 3-column grid: left (fixed), center (flex), right (fixed) */}
      <div
        style={{ gridTemplateColumns: `${sideWidth}px minmax(0,1fr) ${sideWidth}px` }}
        className={[
          "w-full min-h-16 py-2 grid items-center gap-3",
          rowClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* LEFT */}
        <div ref={leftSlotRef} className="flex min-w-0 items-center justify-start">
          {showBack ? (
            <button
              type="button"
              aria-label={backLabel || (close ? "Close" : "Back")}
              onClick={onBack}
              className={leftBtnClasses}
            >
              {backIconSrc ? (
                <img src={backIconSrc} alt="" className="h-6 w-6" draggable="false" />
              ) : close ? (
                <XIcon className="h-5 w-5" aria-hidden />
              ) : (
                <ChevronLeft className="h-5 w-5" aria-hidden />
              )}
            </button>
          ) : (
            <span aria-hidden="true" className="block h-10 w-10" />
          )}
        </div>

        {/* CENTER (truncate-safe, cannot overlap left/right) */}
        <div className="min-w-0 text-center">
          <h1
            className={[
              wrapTitle ? "text-center whitespace-normal break-words leading-tight" : "text-center truncate",
              // ✅ optional mobile clamp to keep extra-long titles from feeling “too wide”
              "max-w-[22ch] mx-auto sm:max-w-none",
              titleClassName,
            ].join(" ")}
          >
            {title}
          </h1>

          {subtitle ? (
            <p
              className={[
                wrapTitle ? "mt-1 text-center whitespace-normal break-words" : "mt-1 text-center truncate",
                "max-w-[28ch] mx-auto sm:max-w-none",
                subtitleClassName,
              ].join(" ")}
            >
              {subtitle}
            </p>
          ) : null}
        </div>

      {/* RIGHT */}
        <div ref={rightSlotRef} className="flex min-w-0 items-center justify-end">
          {rightSlot}
        </div>
      </div>
    </header>
  );
}
